import React, { useEffect, useState } from 'react';
import { Redirect as ReRoute, Route, Router, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import 'App';
import * as Pages from 'pages';
import { createBrowserHistory } from 'history';
import { Footer, Header, UnifiedPlatformFooter } from 'components';
import { isIE } from 'react-device-detect';
import styles from './style.module.css';
import { useConfig } from 'hooks';

const PrivateRoute = ({ component: Component, ...rest }) => {
  const jwt = useSelector((state) => state.auth.authenticated);
  return (
    <Route
      {...rest}
      render={(props) =>
        jwt !== null ? (
          <Component {...props} />
        ) : (
          <ReRoute
            to={{
              pathname: '/pharmacy/logout',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

export const browserHistory = createBrowserHistory();

const App = () => {
  const [getConfig] = useConfig();
  useEffect(() => {
    async function wrappedGetConfig() {
      await getConfig();
    }
    wrappedGetConfig();
  });

  const altBackgroundPaths = [
    window.PUBLIC_URL + '/dashboard/add-medication',
    window.PUBLIC_URL + '/dashboard/add-medication/select',
    window.PUBLIC_URL + '/dashboard',
    window.PUBLIC_URL + '/dashboard/exemption',
    window.PUBLIC_URL + '/dashboard/exemption/saved',
    window.PUBLIC_URL + '/dashboard/order',
    window.PUBLIC_URL + '/dashboard/order-reminder',
    window.PUBLIC_URL + '/dashboard/delivery-options',
    window.PUBLIC_URL + '/dashboard/delivery-details',
    window.PUBLIC_URL + '/dashboard/find-pharmacy',
    window.PUBLIC_URL + '/dashboard/find-pharmacy/select',
    window.PUBLIC_URL + '/dashboard/pharmacy-delivery',
    window.PUBLIC_URL + '/dashboard/payment',
    window.PUBLIC_URL + '/dashboard/payment/new',
    window.PUBLIC_URL + '/dashboard/basket',
    window.PUBLIC_URL + '/dashboard/confirmation',
    window.PUBLIC_URL + '/dashboard/account',
    window.PUBLIC_URL + '/dashboard/account/address',
    window.PUBLIC_URL + '/dashboard/account/address/edit',
    window.PUBLIC_URL + '/dashboard/account/address/new',
    window.PUBLIC_URL + '/dashboard/account/address/postcode',
    window.PUBLIC_URL + '/dashboard/email-preference',
    window.PUBLIC_URL + '/dashboard/notification-permissions',
    window.PUBLIC_URL + '/dashboard/consent',
    window.PUBLIC_URL + '/dashboard/manual-order',
    window.PUBLIC_URL + '/dashboard/manual-basket',
    window.PUBLIC_URL + '/dashboard/nhs-settings',
    window.PUBLIC_URL + '/error',
    window.PUBLIC_URL + '/nhs-login-redirect',
    window.PUBLIC_URL + '/tooltip',
    window.PUBLIC_URL + '/logout',
  ];

  const isPathPrivate = (path) => {
    path = path.replace(/\/\d*$/, '');
    if (path.charAt(path.length - 1) === '/') {
      path = path.slice(0, -1);
    }
    return altBackgroundPaths.includes(path);
  };

  const [privateAppStyle, setPrivateAppStyle] = useState(isPathPrivate(browserHistory.location.pathname));

  browserHistory.listen((location, action) => {
    window.scrollTo(0, 0);
    if (isPathPrivate(location.pathname)) {
      setPrivateAppStyle(true);
    } else {
      setPrivateAppStyle(false);
    }
  });

  if (isIE)
    return (
      <div className={`App ${privateAppStyle ? styles.appAltBackground : styles.app}`}>
        <Router history={browserHistory}>
          <Header noSignIn />
          <div className="coop-l-container">
            <div style={{ padding: '3rem 0 5rem 0' }}>
              <p>
                We are sorry but we no longer support your browser. We support quite a few other browsers which you
                could download to continue using Hey Pharmacist:
              </p>
              <ul>
                <li>
                  <a href="https://www.microsoft.com/en-us/edge" target="_blank" rel="noreferrer noopener">
                    Edge
                  </a>
                </li>
                <li>
                  <a href="https://www.google.co.uk/chrome/" target="_blank" rel="noreferrer noopener">
                    Chrome
                  </a>
                </li>
                <li>
                  <a href="https://www.mozilla.org/en-US/firefox/new/" target="_blank" rel="noreferrer noopener">
                    Firefox
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <UnifiedPlatformFooter />
        </Router>
      </div>
    );
  return (
    <div className={`App ${privateAppStyle ? styles.appAltBackground : styles.app}`}>
      <Router history={browserHistory}>
        <Header />
        <div>
          <div className="coop-l-container">
            <div id="navigation" />
          </div>
          <Switch>
            <Route path="/pharmacy" exact component={Pages.Login} />
            <Route path="/pharmacy/login/mobile" exact component={Pages.LoginMobile} />
            <Route path="/pharmacy/login/email" exact component={Pages.LoginEmail} />
            <Route path="/pharmacy/login" exact component={Pages.Login} />
            <Route path="/pharmacy/nhs-login-consent" exact component={Pages.NHSLoginRedirect} />
            <Route path="/pharmacy/logout" exact component={Pages.Logout} />
            <Route path="/pharmacy/register" exact component={Pages.Register} />
            <Route path="/pharmacy/redirect" exact component={Pages.Redirect} />
            <Route path="/pharmacy/tooltip/:id" exact component={Pages.Tooltip} />

            {window.PROD_FEATURES === 'false' && [
              <Route
                key="register/P5"
                path="/pharmacy/register/P5"
                exact
                render={(props) => <Pages.Register {...props} isP9={false} />}
              />,
              <PrivateRoute
                key="dashboard/add-medication"
                path="/pharmacy/dashboard/add-medication"
                exact
                component={Pages.AddMedication}
              />,
              <PrivateRoute
                key="dashboard/add-medication/select"
                path="/pharmacy/dashboard/add-medication/select"
                exact
                component={Pages.AddMedicationSelect}
              />,
            ]}

            <PrivateRoute path="/pharmacy/dashboard" exact component={Pages.Dashboard} />
            <PrivateRoute path="/pharmacy/dashboard/exemption" exact component={Pages.ExemptionsPage} />
            <PrivateRoute path="/pharmacy/dashboard/exemption/saved" exact component={Pages.ExemptionSaved} />
            <PrivateRoute path="/pharmacy/dashboard/order" exact component={Pages.Order} />
            <PrivateRoute path="/pharmacy/dashboard/order/:id" exact component={Pages.OrderById} />
            <PrivateRoute path="/pharmacy/dashboard/order-reminder/:id" exact component={Pages.CreateOrderReminder} />
            <PrivateRoute path="/pharmacy/dashboard/order-reminder/" exact component={Pages.OrderReminders} />
            <PrivateRoute path="/pharmacy/dashboard/delivery-options" exact component={Pages.DeliveryOptions} />
            <PrivateRoute path="/pharmacy/dashboard/delivery-details" exact component={Pages.DeliveryDetails} />
            <PrivateRoute path="/pharmacy/dashboard/find-pharmacy" exact component={Pages.FindPharmacyByPostcode} />
            <PrivateRoute path="/pharmacy/dashboard/find-pharmacy/select" exact component={Pages.SelectPharmacy} />
            <PrivateRoute path="/pharmacy/dashboard/pharmacy-delivery" exact component={Pages.PharmacyDelivery} />
            <PrivateRoute path="/pharmacy/dashboard/payment" exact component={Pages.Payment} />
            <PrivateRoute path="/pharmacy/dashboard/payment/new" exact component={Pages.AddNewCard} />
            <PrivateRoute path="/pharmacy/dashboard/basket" exact component={Pages.Basket} />
            <PrivateRoute path="/pharmacy/dashboard/confirmation" exact component={Pages.Confirmation} />
            <PrivateRoute path="/pharmacy/dashboard/account" exact component={Pages.Account} />
            <PrivateRoute path="/pharmacy/dashboard/account/address" exact component={Pages.Address} />
            <PrivateRoute path="/pharmacy/dashboard/account/address/new" exact component={Pages.AddNewAddress} />
            <PrivateRoute path="/pharmacy/dashboard/account/address/edit" exact component={Pages.EditAddress} />
            <PrivateRoute
              path="/pharmacy/dashboard/account/address/postcode"
              exact
              component={Pages.AddNewAddressByPostcode}
            />
            <PrivateRoute path="/pharmacy/dashboard/email-preference" exact component={Pages.EmailPreferences} />
            <PrivateRoute
              path="/pharmacy/dashboard/notification-permissions"
              exact
              component={Pages.NotificationPermissions}
            />
            <PrivateRoute path="/pharmacy/dashboard/consent" exact component={Pages.Consent} />
            <PrivateRoute path="/pharmacy/dashboard/manual-order" exact component={Pages.ManualOrder} />
            <PrivateRoute path="/pharmacy/dashboard/manual-basket" exact component={Pages.ManualBasket} />
            <PrivateRoute path="/pharmacy/dashboard/nhs-settings" exact component={Pages.NHSSettings} />
            <Route path="/pharmacy/error" exact component={Pages.Error} />

            <Route path="/pharmacy/404" exact component={Pages.NotFound} />
            <ReRoute from="/pharmacy/*" to="/pharmacy/404" />
            <Route
              path="/"
              render={({ location }) => {
                // Reroute to the brochure site if anything not under /pharmacy is detected.
                // Prevent rerouting if the browser has already been rerouted.
                if (!location.search.includes('redir=1') && window) {
                  window.location.replace(location.pathname + '?redir=1');
                  return null;
                }
                return <ReRoute to="/pharmacy/404" />;
              }}
            />
          </Switch>
        </div>
        {privateAppStyle ? <Footer /> : <UnifiedPlatformFooter />}
      </Router>
    </div>
  );
};

export default App;
